import React from 'react';
import { Link } from 'react-router-dom';
import EmailSignup from '../components/EmailSignup';
import companyLogo from '../assets/company-logo.png';
import './Home.css';

const Home: React.FC = () => {
  const platformLinks = [
    { to: '/', label: 'Home' },
    { to: '/profile-builder', label: 'Profile Builder' },
    { to: '/essay-builder', label: 'Essay Analysis' },
    { to: '/admissions-chatbot', label: 'Admissions Assistant' },
    { to: '/roadmap', label: 'Applicant Roadmap' },
    { to: '/application-portal', label: 'Application Portal' }
  ];

  const moreToolLinks = [
    { to: '/resume-review', label: 'Resume Review' },
    { to: '/school-intel', label: 'School Intel (Beta)' },
    { to: '/odds-calculator', label: 'Admissions Odds' },
    { to: '/school-comparison', label: 'School Comparison' },
    { to: '/study-plan', label: 'Study Planner' },
    { to: '/calendar', label: 'Calendar' }
  ];

  const accountLinks = [
    { to: '/account', label: 'My Account' },
    { to: '/signup', label: 'Sign Up' },
    { to: '/login', label: 'Log In' },
    { to: '/forum', label: 'Forum' },
    { to: '/blog', label: 'Blog' }
  ];

  const aboutLinks = [
    { to: '/support', label: 'Support' },
    { to: '/legal', label: 'Legal' },
    { to: '/privacy', label: 'Privacy' }
  ];

  return (
    <div className="home">
      <div className="home-container">
        <div className="home-hero-content">
          <h1 className="home-title">
            Expanding Access to Deferred MBA Admissions
          </h1>
          <p className="home-description">
            Deferredly combines premium AI-powered tools with affordable admissions consulting 
            to help students succeed in applying to deferred MBA programs.
          </p>
          
          <EmailSignup />
        </div>
        
        <section className="pricing-section" aria-label="Pricing plans">
          <h2 className="pricing-title">Choose the Deferredly plan that fits your journey</h2>
          <p className="pricing-subtitle">
            Start free, or upgrade to Pro with monthly or annual billing.
          </p>

          <div className="pricing-grid">
            <article className="pricing-card">
              <header className="pricing-card-head">
                <h3 className="pricing-card-title">Free Plan</h3>
              </header>
              <p className="pricing-card-price">$0</p>
              <p className="pricing-card-billing">forever</p>
              <ul className="pricing-card-list">
                <li>Core planning tools</li>
                <li>Basic application timeline tracking</li>
                <li>Limited AI guidance</li>
              </ul>
              <a href="/signup" className="pricing-card-button pricing-card-button-secondary">
                Get Started Free
              </a>
            </article>

            <article className="pricing-card">
              <header className="pricing-card-head">
                <h3 className="pricing-card-title">Pro — Monthly</h3>
              </header>
              <p className="pricing-card-price">$9</p>
              <p className="pricing-card-billing">per month</p>
              <ul className="pricing-card-list">
                <li>Unlimited essay analysis support</li>
                <li>Advanced school strategy tools</li>
                <li>Priority product updates and features</li>
              </ul>
              <a href="/signup" className="pricing-card-button pricing-card-button-secondary">
                Upgrade to Pro
              </a>
            </article>

            <article className="pricing-card pricing-card-featured">
              <header className="pricing-card-head">
                <h3 className="pricing-card-title">Pro — Annual</h3>
              </header>
              <p className="pricing-card-price">$49</p>
              <p className="pricing-card-billing">per year</p>
              <ul className="pricing-card-list">
                <li>Everything in Pro Monthly</li>
                <li>Best value for committed applicants</li>
                <li>One simple annual payment</li>
              </ul>
              <a href="/signup" className="pricing-card-button pricing-card-button-secondary">
                Choose Annual Pro
              </a>
            </article>
          </div>

          <p className="pricing-value-callout">
            Traditional MBA admissions consulting can cost hundreds per hour. Deferredly Pro is $49 for an entire year.
          </p>
        </section>

        <section className="home-cta-section" aria-label="Get started with Deferredly">
          <h2 className="home-cta-title">Ready to get started?</h2>
          <p className="home-cta-description">
            Build your profile, pressure-test your story, and get personalized guidance for deferred MBA admissions.
          </p>
          <Link to="/signup" className="home-cta-button">
            Get Started
          </Link>
        </section>

      </div>
      <footer className="home-footer" aria-label="Site footer">
        <div className="home-footer-inner">
          <section className="home-footer-brand" aria-label="Deferredly company">
            <Link to="/" className="home-footer-logo" aria-label="Deferredly home">
              <img src={companyLogo} alt="" />
              <span>Deferredly</span>
            </Link>
            <a
              href="https://www.linkedin.com/company/deferredly"
              target="_blank"
              rel="noreferrer noopener"
              className="home-footer-social"
            >
              <svg
                className="home-footer-social-icon"
                viewBox="0 0 24 24"
                aria-hidden="true"
                focusable="false"
              >
                <path d="M19 3A2 2 0 0 1 21 5V19A2 2 0 0 1 19 21H5A2 2 0 0 1 3 19V5A2 2 0 0 1 5 3H19ZM8.34 17V9.68H6V17H8.34ZM7.17 8.72A1.36 1.36 0 1 0 7.14 6A1.36 1.36 0 0 0 7.17 8.72ZM18 17V12.98C18 10.83 16.85 9.54 15.03 9.54C13.56 9.54 12.91 10.35 12.54 10.92V9.68H10.2V17H12.54V12.91C12.54 12.69 12.56 12.47 12.62 12.31C12.79 11.86 13.17 11.39 13.82 11.39C14.67 11.39 15 12.03 15 12.96V17H18Z" />
              </svg>
              LinkedIn
            </a>
            <p className="home-footer-meta">Copyright {new Date().getFullYear()} Deferredly. All rights reserved.</p>
          </section>

          <section className="home-footer-group" aria-labelledby="home-footer-platform">
            <h3 id="home-footer-platform">Platform</h3>
            <ul>
              {platformLinks.map((item) => (
                <li key={item.to}>
                  <Link to={item.to}>{item.label}</Link>
                </li>
              ))}
            </ul>
          </section>

          <section className="home-footer-group" aria-labelledby="home-footer-tools">
            <h3 id="home-footer-tools">More Tools</h3>
            <ul>
              {moreToolLinks.map((item) => (
                <li key={item.to}>
                  <Link to={item.to}>{item.label}</Link>
                </li>
              ))}
            </ul>
          </section>

          <section className="home-footer-group" aria-labelledby="home-footer-resources">
            <h3 id="home-footer-resources">Resources</h3>
            <ul>
              {accountLinks.map((item) => (
                <li key={item.to}>
                  <Link to={item.to}>{item.label}</Link>
                </li>
              ))}
            </ul>
          </section>

          <section className="home-footer-group" aria-labelledby="home-footer-about">
            <h3 id="home-footer-about">About</h3>
            <ul>
              {aboutLinks.map((item) => (
                <li key={item.to}>
                  <Link to={item.to}>{item.label}</Link>
                </li>
              ))}
            </ul>
          </section>
        </div>
      </footer>
    </div>
  );
};

export default Home;
